import React from "react";
import { ShieldCheck, ShieldAlert } from "lucide-react";

const GREEN_FG = "#1e5d34";
const GREEN_BG = "#e8f3ec";
const GREEN_BORDER = "#a3d4b2";
const RED_FG = "#8a2a0c";
const RED_BG = "#fff4f0";
const RED_BORDER = "#f3c2b3";

const SEVERITY_COLOR = {
  INFO: { fg: "#1f4d8a", bg: "#eaf1fb", border: "#aac4ec" },
  WARNING: { fg: "#8a5a14", bg: "#fef3e6", border: "#f1c89a" },
  CRITICAL: { fg: "#8a2a0c", bg: "#fff4f0", border: "#f3c2b3" },
};

// Certification badge — green ShieldCheck + bold "CERTIFIED" + optional
// "w/ exceptions" secondary label (smaller, lighter, lower emphasis).
// CERTIFIED WITH EXCEPTIONS is *not* a warning state; it remains green.
export function VerdictBadge({ verdict, dataTestId, compact = false }) {
  if (!verdict) return null;

  const isCertified =
    verdict === "CERTIFIED" || verdict === "CERTIFIED WITH EXCEPTIONS";
  const hasExceptions = verdict === "CERTIFIED WITH EXCEPTIONS";

  const baseFontPx = compact ? 11 : 13;
  const iconPx = Math.round(baseFontPx * 1.15);

  if (isCertified) {
    return (
      <span
        data-testid={dataTestId}
        title={`Governance verdict: ${verdict}`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: baseFontPx,
          fontWeight: 700,
          letterSpacing: "0.04em",
          padding: "3px 10px",
          borderRadius: 999,
          background: GREEN_BG,
          color: GREEN_FG,
          border: `1px solid ${GREEN_BORDER}`,
          lineHeight: 1.1,
        }}
      >
        <ShieldCheck
          size={iconPx}
          strokeWidth={2.25}
          color={GREEN_FG}
          aria-hidden="true"
        />
        <span data-testid={dataTestId ? `${dataTestId}-primary` : undefined}>
          CERTIFIED
        </span>
        {hasExceptions && (
          <span
            data-testid={
              dataTestId ? `${dataTestId}-secondary` : undefined
            }
            style={{
              fontSize: Math.round(baseFontPx * 0.58),
              fontWeight: 400,
              letterSpacing: "0.02em",
              opacity: 0.72,
              textTransform: "lowercase",
              marginLeft: 1,
            }}
          >
            w/ exceptions
          </span>
        )}
      </span>
    );
  }

  // NOT CERTIFIED (and any other non-certified verdict) — kept red.
  return (
    <span
      data-testid={dataTestId}
      title={`Governance verdict: ${verdict}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: baseFontPx,
        fontWeight: 700,
        letterSpacing: "0.05em",
        padding: "3px 10px",
        borderRadius: 999,
        background: RED_BG,
        color: RED_FG,
        border: `1px solid ${RED_BORDER}`,
        lineHeight: 1.1,
      }}
    >
      <ShieldAlert
        size={iconPx}
        strokeWidth={2.25}
        color={RED_FG}
        aria-hidden="true"
      />
      {verdict}
    </span>
  );
}

export function UnattestedBadge({ dataTestId }) {
  return (
    <span
      data-testid={dataTestId}
      title="Period is locked but no certification has been recorded."
      style={{
        display: "inline-block", fontSize: 10, fontWeight: 700,
        letterSpacing: "0.05em", padding: "2px 8px", borderRadius: 999,
        background: "#f4f4f4", color: "#666", border: "1px solid #d4d4d4",
      }}
    >
      UN-ATTESTED
    </span>
  );
}

export function DriftBadge({ drift, dataTestId }) {
  if (!drift || !drift.drifted) return null;
  const c = SEVERITY_COLOR[drift.severity] || SEVERITY_COLOR.INFO;
  const sev = (drift.severity || "INFO").toUpperCase();
  // Plain-language label and tooltip so a Controller can read the badge at a
  // glance without needing to know the underlying severity codes.
  const LABEL = {
    INFO:  "Drift · informational",
    WARN:  "Drift · review",
    BLOCK: "Drift · action required",
  };
  const HINT = {
    INFO:  "Underlying data has moved since this period was attested. " +
           "Informational only — the attestation remains valid.",
    WARN:  "Underlying data has moved since this period was attested. " +
           "Review the changes; re-certification may be required.",
    BLOCK: "Underlying data has moved since this period was attested. " +
           "Re-certification is required before the verdict can stand.",
  };
  const label = LABEL[sev] || `Drift · ${sev.toLowerCase()}`;
  const hint = (HINT[sev] || "Underlying data has moved since this period was attested.")
    + `\n\nAttested: ${drift.attested}\nLive: ${drift.live}\n`
    + (drift.drift_causes || []).map(d => `• ${d}`).join("\n");
  return (
    <span
      data-testid={dataTestId}
      title={hint}
      style={{
        display: "inline-block", fontSize: 10, fontWeight: 700,
        letterSpacing: "0.04em", padding: "2px 8px", borderRadius: 999,
        background: c.bg, color: c.fg, border: `1px solid ${c.border}`,
        marginLeft: 6, textTransform: "uppercase",
      }}
    >
      {label}
    </span>
  );
}
